import {BehaviorSubject} from 'rxjs';
import {filter} from 'rxjs/operators';

const WindowPostMessageProxyModule = require('window-post-message-proxy');
const WindowPostMessageProxy = WindowPostMessageProxyModule.WindowPostMessageProxy || WindowPostMessageProxyModule;

export const ApplicationContextPlatformEnum = {
    itunes: 'itunes',
    android: 'android',
};

export class ChimeraSlave {
    constructor(
        targetWindow,
        options = {
            suppressWarnings: true,
            logMessages: false,
        }
    ) {
        this.targetWindow = targetWindow;
        this.configSubject = new BehaviorSubject(null);
        this.userSubject = new BehaviorSubject(null);
        this.authSubject = new BehaviorSubject(null);
        this.isConnected = false;

        this.proxy = new WindowPostMessageProxy({
            name: 'ChimeraSlaveFrame',
            suppressWarnings: options.suppressWarnings,
            logMessages: options.logMessages,
        });

        this.proxy.addHandler({
            test: this.testMessage,
            handle: this.messageHandler,
        });

        this.proxy.start();
    }

    get configObserver() {
        return this.configSubject.pipe(filter((data) => !!data));
    }

    get config() {
        return this.configSubject.value;
    }

    get userObserver() {
        return this.userSubject.pipe(filter((data) => !!data));
    }

    get user() {
        return this.userSubject.value;
    }

    get auth() {
        return this.authSubject.value;
    }

    send = (message) => {
        return this.proxy.postMessage(this.targetWindow, message);
    };

    async init() {
        try {
            // Add timeout in case parent doesn't respond
            const initPromise = this.send({type: 'chimera:init'});
            const timeoutPromise = new Promise((_, reject) =>
                setTimeout(() => reject(new Error('Init timeout - no response from parent')), 3000)
            );

            const data = await Promise.race([initPromise, timeoutPromise]);

            this.isConnected = !!data?.success;

            if (this.isConnected) {
                await Promise.all([this.getUser(), this.getConfig()]);
            }
            return !!data?.success;
        } catch (error) {
            console.error('Chimera init error:', error);
            this.isConnected = false;
            return false;
        }
    }

    getConfig = async () => {
        try {
            const config = await this.send({type: 'chimera:config'});
            this.configSubject.next(config);
        } catch (error) {
            console.error('Chimera getConfig error:', error);
        }
    };

    getUser = async () => {
        try {
            const user = await this.send({type: 'chimera:user'});
            this.userSubject.next(user);
        } catch (error) {
            console.error('Chimera getUser error:', error);
        }
    };

    testMessage = (message, event) => {
        // Basic message structure validation
        const isValidMessage = (
            !!message &&
            typeof message === 'object' &&
            typeof message.type === 'string' &&
            message.type.startsWith('chimera:')
        );

        if (!isValidMessage) {
            return false;
        }

        return true;
    };

    messageHandler = async (message) => {
        switch (message.type) {
            case 'chimera:config':
                // If parent sends full config payload, use it directly
                if (message.payload != null) {
                    this.configSubject.next(message.payload);
                    return message.payload;
                }
                // Otherwise request config from parent
                return this.getConfig();

            case 'chimera:user':
                // If parent sends full user payload, use it directly
                if (message.payload != null) {
                    this.userSubject.next(message.payload);
                    return message.payload;
                }
                // Otherwise request user from parent
                return this.getUser();

            case 'chimera:auth':
                // Auth payload is always pushed by parent
                this.authSubject.next(message.payload);
                return message.payload;

            default:
                return null;
        }
    };
}

